import React, { FC, useState, useEffect } from "react";
import { Modal } from "flowbite-react";
import { Button } from "@components";
import { handleImageError } from "../../utils/imageUtils";
import { HiOutlinePhotograph, HiTrash } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { modalPortalRoot } from "../../helpers/modalRoot";

export interface ImageListProp {
  imgUrls: string[];
  deletableImgUrls?: string[];
  deletingImgUrl?: string;
  deleteAriaLabel?: string;
  onDelete?: (imgUrl: string) => void;
}

export const ImageList: FC<ImageListProp> = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const { t } = useTranslation(["common", "orders"]);

  const handleImageClick = (imgUrl: string) => {
    setSelectedImage(imgUrl);
    setIsOpen(true);
  };

  useEffect(() => {
    if (!isOpen) {
      setSelectedImage(null);
    }
  }, [isOpen]);

  // Display an icon and text message if no images are available
  if (!props.imgUrls.length) {
    return (
      <div className="flex items-center text-muted py-4">
        <HiOutlinePhotograph className="h-8 w-8 mr-3" />
        <span className="text-base">{t("common:no_images_on_order")}</span>
      </div>
    );
  }

  return (
    <div>
      <div className="flex gap-5 flex-wrap">
        {props.imgUrls.map((x, index) => (
          <div
            key={`img-${index}-${x}`}
            onClick={() => handleImageClick(x)}
            className="relative cursor-pointer"
          >
            <img
              width={96}
              height={96}
              src={x}
              alt={`Orderimage-${index}`}
              className="rounded-md object-cover h-24 w-24 cursor-pointer hover:ring-2 hover:ring-lgb-blue-400 border border-border"
              onError={(e) => handleImageError(e)}
            />
            {props.onDelete && props.deletableImgUrls?.includes(x) && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  props.onDelete?.(x);
                }}
                disabled={props.deletingImgUrl === x}
                className="absolute right-1 top-1 rounded bg-white/90 p-1.5 text-gray-600 shadow hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-800/90 dark:text-gray-300 dark:hover:bg-red-900/80 dark:hover:text-red-200"
                aria-label={props.deleteAriaLabel}
              >
                <HiTrash className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      {selectedImage && (
        <Modal
          root={modalPortalRoot}
          show={isOpen}
          size="xl"
          onClose={() => setIsOpen(false)}
          dismissible={true}
        >
          <Modal.Body className="flex justify-center items-center h-[500px]">
            <img
              src={selectedImage}
              alt="Selected order image"
              className="mx-auto rounded-lg max-h-full max-w-full object-contain"
              onError={(e) => handleImageError(e)}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button className="bg-brand" onClick={() => setIsOpen(false)}>
              {t("common:close")}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};
